import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, FileText, AlertCircle, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { toast } from 'sonner';
import {
  parseTemplateCSV,
  buildExampleTemplateCSV,
  summarizeParsedContent,
  type ParsedTemplateCSV,
} from '@/lib/templateCsv';
import { downloadCSV } from '@/lib/csv';
import { importTemplateFromCSV, type TemplateSet } from '@/lib/api';

type Mode = 'create' | 'update';

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingTemplates: TemplateSet[];
  onImported: (template: TemplateSet) => void;
}

export default function ImportTemplateCSVDialog({ projectId, open, onOpenChange, existingTemplates, onImported }: Props) {
  const [mode, setMode] = useState<Mode>('create');
  const [csvText, setCsvText] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [nameOverride, setNameOverride] = useState<string>('');
  const [language, setLanguage] = useState<string>('fr');
  const [targetId, setTargetId] = useState<string | undefined>(undefined);
  const [parsed, setParsed] = useState<ParsedTemplateCSV | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMode('create');
      setCsvText('');
      setFileName('');
      setNameOverride('');
      setLanguage('fr');
      setTargetId(undefined);
      setParsed(null);
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  // Re-parse whenever the CSV changes
  useEffect(() => {
    if (!csvText.trim()) {
      setParsed(null);
      return;
    }
    try {
      const result = parseTemplateCSV(csvText);
      setParsed(result);
      if (result.meta.language_code && !language) setLanguage(result.meta.language_code);
    } catch (err) {
      console.error('Template CSV parse error:', err);
      setParsed({
        meta: {},
        content: null,
        errors: [(err as Error).message],
        warnings: [],
      });
    }
  }, [csvText]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => (parsed && parsed.errors.length === 0 ? summarizeParsedContent(parsed) : null), [parsed]);
  const hasErrors = !!parsed && parsed.errors.length > 0;
  const canImport = !!parsed && !hasErrors && !importing && (mode === 'create' || !!targetId);

  const handleFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = (e.target?.result as string) ?? '';
      setCsvText(text);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const handleDownloadExample = () => {
    downloadCSV('lunargrid-template-example.csv', buildExampleTemplateCSV());
  };

  const handleImport = async () => {
    if (!parsed || hasErrors) return;
    setImporting(true);
    try {
      const result = await importTemplateFromCSV({
        projectId,
        parsed,
        targetTemplateSetId: mode === 'update' ? targetId : undefined,
        nameOverride: nameOverride.trim() || undefined,
        languageOverride: language || undefined,
      });
      toast.success(mode === 'create' ? `Created template "${result.name}"` : `Updated template "${result.name}"`);
      onImported(result);
      onOpenChange(false);
    } catch (err) {
      console.error('Template CSV import failed:', err);
      toast.error((err as Error).message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="size-5" />
            Import Template from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a LunarGrid Template CSV to create a new template or update an existing one.
            The format mirrors the structure of a TemplateSet, with one row per element.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Mode picker */}
          <div className="space-y-2">
            <Label>Action</Label>
            <RadioGroup value={mode} onValueChange={v => setMode(v as Mode)} className="grid grid-cols-2 gap-2">
              <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer ${mode === 'create' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <RadioGroupItem value="create" />
                <div className="space-y-0.5">
                  <div className="font-medium text-sm">Create new template</div>
                  <div className="text-xs text-muted-foreground">A new TemplateSet will be created in this project.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer ${mode === 'update' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <RadioGroupItem value="update" />
                <div className="space-y-0.5">
                  <div className="font-medium text-sm">Update existing template</div>
                  <div className="text-xs text-muted-foreground">Replace template_content of a chosen template.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {mode === 'update' && (
            <div className="space-y-2">
              <Label htmlFor="target-template">Target template</Label>
              <Select value={targetId ?? '__none__'} onValueChange={v => setTargetId(v === '__none__' ? undefined : v)}>
                <SelectTrigger id="target-template">
                  <SelectValue placeholder="Pick a template to update" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select a template...</SelectItem>
                  {existingTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.language_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The template's headlines, descriptions, keywords, placeholders and meta will be replaced by the CSV contents.
                Generation settings and history are preserved.
              </p>
            </div>
          )}

          {/* File picker */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="csv-file">CSV file</Label>
              <Button type="button" variant="ghost" size="sm" onClick={handleDownloadExample}>
                <Download className="size-3.5 mr-1.5" />
                Download example
              </Button>
            </div>
            <Input
              id="csv-file"
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            {fileName && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <FileText className="size-3.5" />
                {fileName}
              </div>
            )}
          </div>

          {/* Create-mode metadata */}
          {mode === 'create' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="name-override">Name override (optional)</Label>
                <Input
                  id="name-override"
                  placeholder={parsed?.meta.name ?? 'From Meta:name in CSV'}
                  value={nameOverride}
                  onChange={e => setNameOverride(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lang">Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger id="lang">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">French (fr)</SelectItem>
                    <SelectItem value="en">English (en)</SelectItem>
                    <SelectItem value="es">Spanish (es)</SelectItem>
                    <SelectItem value="de">German (de)</SelectItem>
                    <SelectItem value="it">Italian (it)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Validation feedback */}
          {parsed && (
            <div className="space-y-3">
              {hasErrors && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>{parsed.errors.length} error{parsed.errors.length > 1 ? 's' : ''} — fix and re-upload</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4 mt-2 space-y-0.5 text-xs max-h-40 overflow-y-auto">
                      {parsed.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {!hasErrors && summary && (
                <Alert>
                  <CheckCircle2 className="size-4" />
                  <AlertTitle>Ready to import</AlertTitle>
                  <AlertDescription>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="secondary">{summary.headlineSlotsFilled}/15 headlines</Badge>
                      <Badge variant="secondary">{summary.descriptionSlotsFilled}/4 descriptions</Badge>
                      <Badge variant="secondary">{summary.keywordCount} keywords</Badge>
                      <Badge variant="secondary">{summary.placeholderCount} placeholders</Badge>
                      {summary.adCount > 1 && <Badge variant="secondary">{summary.adCount} ads</Badge>}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {parsed.warnings.length > 0 && (
                <Alert>
                  <AlertTriangle className="size-4" />
                  <AlertTitle>{parsed.warnings.length} warning{parsed.warnings.length > 1 ? 's' : ''}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4 mt-2 space-y-0.5 text-xs max-h-40 overflow-y-auto">
                      {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!canImport}>
            {importing ? 'Importing...' : mode === 'create' ? 'Create template' : 'Update template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
